import {
  Bot,
  Brain,
  Cog,
  DollarSign,
  FileJson,
  Layout,
  Search,
  Server,
  Sparkles,
  Eye,
  Wrench,
  Zap,
  Copy,
  Check,
} from 'lucide-react';
import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import type { AgentInfo } from '@/types';

const agentIcons: Record<string, typeof Bot> = {
  orchestrator: Cog,
  oracle: Brain,
  planning: Sparkles,
  frontend: Layout,
  backend: Server,
  mocking: FileJson,
  explorer: Search,
  reviewer: Eye,
  search: Search,
  general_chat: Bot,
};

const modelPresetColors: Record<string, string> = {
  'claude-opus': 'bg-purple-500',
  'claude-sonnet': 'bg-blue-500',
  'claude-haiku': 'bg-green-500',
  'gpt-pro': 'bg-emerald-600',
  'gpt-standard': 'bg-emerald-500',
  'gpt-chat': 'bg-teal-500',
  'gpt-mini': 'bg-cyan-500',
  'gemini-pro': 'bg-red-500',
  'gemini-flash': 'bg-orange-500',
};

const modelPresetLabels: Record<string, string> = {
  'claude-opus': 'Claude Opus',
  'claude-sonnet': 'Claude Sonnet',
  'claude-haiku': 'Claude Haiku',
  'gpt-pro': 'GPT Pro',
  'gpt-standard': 'GPT Standard',
  'gpt-chat': 'GPT Chat',
  'gpt-mini': 'GPT Mini',
  'gemini-pro': 'Gemini Pro',
  'gemini-flash': 'Gemini Flash',
};

const toolCategoryLabels: Record<string, string> = {
  all: '모든 도구',
  file: '파일 관리',
  code: '코드 분석',
  analysis: '분석',
  web: '웹 검색',
  search: '검색',
  database: '데이터베이스',
  utility: '유틸리티',
  project: '프로젝트',
};

const toolCategoryIcons: Record<string, typeof Wrench> = {
  all: Zap,
  file: FileJson,
  code: Layout,
  analysis: Brain,
  web: Search,
  search: Search,
  database: Server,
  utility: Wrench,
  project: Cog,
};

interface AgentDetailSheetProps {
  agent: AgentInfo | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AgentDetailSheet({ agent, open, onOpenChange }: AgentDetailSheetProps) {
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [isPromptOpen, setIsPromptOpen] = useState(true);

  if (!agent) return null;

  const IconComponent = agentIcons[agent.type] || Bot;
  const modelColor = agent.modelTier ? modelPresetColors[agent.modelTier] || 'bg-gray-500' : 'bg-gray-500';
  const modelLabel = agent.modelTier ? modelPresetLabels[agent.modelTier] || agent.modelTier : '-';

  const getCostTierColor = (costTier: string | undefined) => {
    switch (costTier) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getCostTierLabel = (costTier: string | undefined) => {
    switch (costTier) {
      case 'high':
        return '높음';
      case 'medium':
        return '보통';
      case 'low':
        return '낮음';
      default:
        return '-';
    }
  };

  const handleCopyPrompt = async () => {
    if (agent.promptTemplate) {
      await navigator.clipboard.writeText(agent.promptTemplate);
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 2000);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <IconComponent className="h-6 w-6 text-primary" />
            </div>
            <div>
              <SheetTitle className="text-xl">
                {(agent.nameKo || agent.name).replace(' Agent', '')}
              </SheetTitle>
              <SheetDescription className="text-sm">
                {agent.type}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-6">
          {/* 기본 정보 */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">기본 정보</h3>
            <div className="space-y-3">
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <p className="text-sm">{agent.descriptionKo || agent.description}</p>
                {agent.recommendedForKo && (
                  <div className="pt-2 border-t border-border/50">
                    <span className="text-xs text-muted-foreground">권장 용도: </span>
                    <span className="text-xs">{agent.recommendedForKo}</span>
                  </div>
                )}
              </div>
            </div>
          </section>

          <Separator />

          {/* 모델 설정 */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">모델 설정</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">모델 프리셋</div>
                <Badge className={`${modelColor} text-white`}>
                  {modelLabel}
                </Badge>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">비용 등급</div>
                <Badge variant="outline" className={getCostTierColor(agent.costTier)}>
                  <DollarSign className="h-3 w-3 mr-1" />
                  {getCostTierLabel(agent.costTier)}
                </Badge>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg col-span-2">
                <div className="text-xs text-muted-foreground mb-1">실제 모델</div>
                <code className="text-xs font-mono bg-background px-2 py-1 rounded">
                  {agent.actualModel || '-'}
                </code>
                {agent.provider && (
                  <span className="text-xs text-muted-foreground ml-2 capitalize">
                    ({agent.provider})
                  </span>
                )}
              </div>
            </div>
          </section>

          <Separator />

          {/* 기능 (Capabilities) */}
          {agent.capabilities && agent.capabilities.length > 0 && (
            <>
              <section>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">기능 (Capabilities)</h3>
                <div className="flex flex-wrap gap-2">
                  {agent.capabilities.map((cap) => (
                    <Badge key={cap} variant="secondary" className="text-xs">
                      {cap.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              </section>
              <Separator />
            </>
          )}

          {/* 활성화된 도구 */}
          {agent.toolsEnabled && agent.toolsEnabled.length > 0 && (
            <>
              <section>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">활성화된 도구</h3>
                <div className="flex flex-wrap gap-2">
                  {agent.toolsEnabled.map((tool) => {
                    const ToolIcon = toolCategoryIcons[tool] || Wrench;
                    return (
                      <Badge key={tool} variant="outline" className="text-xs flex items-center gap-1">
                        <ToolIcon className="h-3 w-3" />
                        {toolCategoryLabels[tool] || tool}
                      </Badge>
                    );
                  })}
                </div>
              </section>
              <Separator />
            </>
          )}

          {/* 프롬프트 템플릿 */}
          <section>
            <Collapsible open={isPromptOpen} onOpenChange={setIsPromptOpen}>
              <CollapsibleTrigger className="flex items-center justify-between w-full">
                <h3 className="text-sm font-semibold text-muted-foreground">시스템 프롬프트</h3>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isPromptOpen ? 'rotate-180' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3">
                {agent.promptTemplate ? (
                  <div className="relative">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 z-10"
                      onClick={handleCopyPrompt}
                    >
                      {copiedPrompt ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                    <pre className="p-4 bg-muted rounded-lg text-xs font-mono overflow-x-auto max-h-80 whitespace-pre-wrap">
                      {agent.promptTemplate}
                    </pre>
                  </div>
                ) : (
                  <div className="p-4 bg-muted/50 rounded-lg text-center text-sm text-muted-foreground">
                    <p>프롬프트 템플릿이 설정되지 않았습니다.</p>
                    <p className="text-xs mt-1">
                      ai-engine/src/agents/config.py에서 prompt_template을 추가하세요.
                    </p>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </section>

          {/* 설정 소스 정보 */}
          {agent.configSource && (
            <>
              <Separator />
              <section>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">설정 정보</h3>
                <div className="p-3 bg-muted/50 rounded-lg text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">설정 소스:</span>
                    <Badge variant="outline" className="text-xs">
                      {agent.configSource === 'file' ? '설정 파일' : '환경 변수'}
                    </Badge>
                  </div>
                  {agent.configPath && (
                    <div className="mt-2">
                      <span className="text-muted-foreground">경로: </span>
                      <code className="font-mono bg-background px-1 rounded">{agent.configPath}</code>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
